import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

// styled components
import { StyledViewCountTooltip } from "./StyledViewCountTooltip";

// i18n
import { useTranslation } from "react-i18next";

// plugin
import clsx from "classnames";

// 報表頁路由（AM-176）
export const REPORT_PATH = "/report";

let popoverSeq = 0;

/**
 * 物件卡片的閱覽數＋說明浮層：閱覽數每小時更新（AM-187），並可前往報表頁。
 * children 是閱覽數本身（圖示＋數字），會包在可聚焦的 button 裡：
 * - 滑鼠：hover 開啟、移開關閉
 * - 觸控／滑鼠點擊、鍵盤 Enter / Space：開啟（再 Tab 即可聚焦報表連結）
 * - Escape、焦點離開、點擊外部：關閉
 * 浮層內含連結，因此不用 role="tooltip"，而是 aria-expanded / aria-controls 的 disclosure。
 */
const ViewCountTooltip = ({ children }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [popoverId] = useState(() => `viewCountPopover-${++popoverSeq}`);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const handleKeyDown = (e) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      if (wrapperRef.current?.contains(document.activeElement)) triggerRef.current?.focus();
    };

    document.addEventListener("mousedown", handlePointerOutside);
    document.addEventListener("touchstart", handlePointerOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerOutside);
      document.removeEventListener("touchstart", handlePointerOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  // 焦點移出整個元件（例如 Tab 離開）才關閉；在 button 與連結之間移動不關閉
  const handleBlur = (e) => {
    if (!wrapperRef.current?.contains(e.relatedTarget)) setOpen(false);
  };

  // 避免點擊閱覽數或浮層（含報表連結）時觸發卡片本身的 onClick（進入編輯器）
  const stopPropagation = (e) => e.stopPropagation();

  return (
    <StyledViewCountTooltip
      ref={wrapperRef}
      className="viewCountTooltip"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={handleBlur}
      onClick={stopPropagation}
    >
      <button
        ref={triggerRef}
        type="button"
        className="viewCountTrigger"
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => setOpen(true)}
      >
        {/* 圖示沒有文字語意，補上隱藏文字讓 screen reader 讀成「瀏覽次數 123」 */}
        <span className="visuallyHidden">{t("viewers")} </span>
        {children}
      </button>
      <div
        id={popoverId}
        role="group"
        aria-label={t("viewers")}
        className={clsx("viewCountPopover", { isVisible: open })}
      >
        <div className="popoverBody">
          <div className="popoverTitle">{t("viewers")}</div>
          <div className="popoverNote">{t("viewersUpdateNote")}</div>
          <Link to={REPORT_PATH} className="popoverLink">
            {t("viewersReportLink")}
          </Link>
        </div>
        <div className="arrowDown" />
      </div>
    </StyledViewCountTooltip>
  );
};

export default ViewCountTooltip;
